// packages/studio/web/src/repos/RepoSettings.tsx
//
// /:slug/:repo/_/settings — the trunk branch name and the feature-branch
// naming pattern for proposals. Both stay abstracted in the rest of the UI.

import { FormEvent, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api/client";
import { wsHome } from "../lib/paths";
import { useWorkspace } from "../api/hooks/useWorkspace";
import { PageHeader } from "../ui/PageHeader";
import { Spinner } from "../ui/Spinner";
import { Banner } from "../ui/Banner";
import { Field } from "../ui/Field";
import { TextInput } from "../ui/TextInput";
import { Button } from "../ui/Button";
import { useToast } from "../ui/Toast";
import { useTranslation, apiErrorMessage } from "../i18n";

interface RepoSettings {
  trunkBranch: string;
  featureBranchPattern: string;
  protectTrunk: boolean;
}

function previewBranch(pattern: string): string {
  return pattern
    .replace(/\{author\}/g, "jane")
    .replace(/\{slug\}/g, "add-billing")
    .replace(/\{id\}/g, "01ktab")
    .replace(/\{date\}/g, "2026-06-03");
}

export function RepoSettingsPage() {
  const { t } = useTranslation("repos");
  const { t: tErr } = useTranslation("errors");
  const { t: tCommon } = useTranslation("common");
  const { slug = "", repo = "" } = useParams();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  // Settings + delete are repo:admin, which the policy grants to owners only.
  const isOwner = useWorkspace(slug).data?.role === "owner";
  const [confirmDelete, setConfirmDelete] = useState("");

  const query = useQuery({
    queryKey: ["repo-settings", slug, repo],
    queryFn: () =>
      apiFetch<RepoSettings>(`/api/ws/${encodeURIComponent(slug)}/r/${encodeURIComponent(repo)}/settings`),
  });

  const [trunk, setTrunk] = useState("");
  const [pattern, setPattern] = useState("");
  const [protectTrunk, setProtectTrunk] = useState(true);
  useEffect(() => {
    if (query.data) {
      setTrunk(query.data.trunkBranch);
      setPattern(query.data.featureBranchPattern);
      setProtectTrunk(query.data.protectTrunk);
    }
  }, [query.data]);

  const save = useMutation({
    mutationFn: (body: Partial<RepoSettings>) =>
      apiFetch<RepoSettings>(`/api/ws/${encodeURIComponent(slug)}/r/${encodeURIComponent(repo)}/settings`, {
        method: "PATCH",
        body,
      }),
    onSuccess: (data) => {
      toast.success(t("settings.toastSaved"));
      qc.setQueryData(["repo-settings", slug, repo], data);
    },
    onError: (err: Error) => toast.error(apiErrorMessage(err, tErr)),
  });

  const deleteRepo = useMutation({
    mutationFn: () =>
      apiFetch<void>(
        `/api/ws/${encodeURIComponent(slug)}/r/${encodeURIComponent(repo)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      toast.success(t("settings.toastDeleted", { repo }));
      qc.invalidateQueries({ queryKey: ["repos", slug] });
      navigate(wsHome(slug));
    },
    onError: (err: Error) => toast.error(apiErrorMessage(err, tErr)),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate({ trunkBranch: trunk.trim(), featureBranchPattern: pattern.trim(), protectTrunk });
  }

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-slate-400">
        <Spinner /> {tCommon("loading")}
      </div>
    );
  }
  if (query.error) {
    return (
      <div className="p-6">
        <Banner kind="error">{apiErrorMessage(query.error, tErr)}</Banner>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-xl p-6">
      <PageHeader
        title={t("settings.title")}
        description={<span className="font-mono text-xs text-slate-500">{repo}</span>}
      />

      {!isOwner && (
        <Banner kind="info" className="mb-4">
          {t("settings.membersReadOnly")}
        </Banner>
      )}

      <form onSubmit={handleSubmit} className="space-y-5 rounded-xl border border-slate-800 bg-slate-900 p-6" noValidate>
        <Field label={t("settings.trunkBranchLabel")}>
          {({ id }) => (
            <TextInput id={id} value={trunk} onChange={(e) => setTrunk(e.target.value)} placeholder={t("settings.trunkBranchPlaceholder")} />
          )}
        </Field>

        <Field label={t("settings.featureBranchPatternLabel")}>
          {({ id }) => (
            <TextInput
              id={id}
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="studio/{author}/{slug}-{id}"
            />
          )}
        </Field>
        <p className="-mt-3 text-xs text-slate-500">
          {t("settings.tokensHint")} <code className="font-mono">{"{author} {slug} {id} {date}"}</code>. {t("settings.exampleLabel")}{" "}
          <span className="font-mono text-slate-400">{previewBranch(pattern || "studio/{author}/{slug}-{id}")}</span>
        </p>

        <label className="flex items-start gap-3 rounded-md border border-slate-800 bg-slate-950/40 p-3">
          <input
            type="checkbox"
            checked={protectTrunk}
            onChange={(e) => setProtectTrunk(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-600 bg-slate-900 text-arch-500"
          />
          <span className="text-sm">
            <span className="font-medium text-slate-200">{t("settings.protectTrunk")}</span>
            <span className="block text-xs text-slate-500">
              {t("settings.protectTrunkDescription", { branch: trunk || t("settings.trunkFallback") })}
            </span>
          </span>
        </label>

        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            disabled={!isOwner || save.isPending || !trunk.trim() || !pattern.trim()}
          >
            {save.isPending ? t("settings.saving") : t("settings.save")}
          </Button>
        </div>
      </form>

      {isOwner && (
        <section className="mt-8 rounded-xl border border-red-900/60 bg-red-950/20 p-6">
          <h2 className="text-sm font-semibold text-red-300">{t("settings.dangerZone")}</h2>
          <p className="mt-1 text-xs text-slate-400">
            {t("settings.dangerDescription")}
          </p>
          <div className="mt-4 space-y-2">
            <Field label={t("settings.confirmLabel", { repo })}>
              {({ id }) => (
                <TextInput
                  id={id}
                  value={confirmDelete}
                  onChange={(e) => setConfirmDelete(e.target.value)}
                  placeholder={repo}
                />
              )}
            </Field>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="danger"
                disabled={confirmDelete !== repo || deleteRepo.isPending}
                onClick={() => deleteRepo.mutate()}
              >
                {deleteRepo.isPending ? t("settings.deleting") : t("settings.deleteRepository")}
              </Button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
